import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  Download,
  ExternalLink,
  ListChecks,
  Loader2,
  MessageSquare,
  Share2,
  Sparkles,
} from "lucide-react";

import { fetchCheck, fetchCheckStatus, API } from "@/lib/api";
import ScoreGauge from "@/components/ScoreGauge";
import FindingItem from "@/components/FindingItem";
import { SEVERITY } from "@/lib/checker-icons";
import { useToast } from "@/hooks/use-toast";

const SEV_ORDER = ["critical", "high", "medium", "low", "ok"];

export default function Result() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isFresh = params.get("fresh") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [statusObj, setStatusObj] = useState(null);
  const [sharedFeedback, setSharedFeedback] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const d = await fetchCheck(id);
      setData(d);
      if (d?.analysis_status === "completed") return true;
    } catch (err) {
      if (err?.response?.status === 410) setError("Dit rapport is verlopen (>30 dagen).");
      // 404 may mean still in queue, ignore
    }
    return false;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    async function tick() {
      if (cancelled) return;
      const done = await load();
      try {
        const s = await fetchCheckStatus(id);
        if (cancelled) return;
        setStatusObj(s);
        if (done || s.ready) return;
      } catch (e) { /* ignore */ }
      timer = setTimeout(tick, 3000);
    }
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [id, load]);

  const grouped = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const f of data.findings || []) {
      const sev = SEV_ORDER.includes(f.severity) ? f.severity : "medium";
      if (!map[sev]) map[sev] = [];
      map[sev].push(f);
    }
    return map;
  }, [data]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-ink">Rapport niet beschikbaar</h1>
        <p className="text-ink-soft mt-2">{error}</p>
        <Link to="/" className="inline-block mt-6 text-[#002FA7] font-semibold">← Terug naar overzicht</Link>
      </div>
    );
  }

  // Waiting / processing state
  if (!data || data.analysis_status !== "completed") {
    return (
      <div className="bg-secondary min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#002FA7] text-white">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
          <h1 className="font-display text-3xl font-bold text-ink mt-8 tracking-tight">
            Je document wordt nu doorgelicht…
          </h1>
          <p className="text-ink-soft mt-3 min-h-[24px]">
            {statusObj?.payment_status === "pending" && "Betaling wordt bevestigd…"}
            {statusObj?.payment_status === "paid" && (statusObj?.analysis_status === "queued" || statusObj?.analysis_status === "running") && "We vergelijken je document met de wetgeving en markttarieven. Dit duurt 30–90 seconden."}
            {!statusObj && (isFresh ? "Even geduld — we starten de controle." : "We zoeken je rapport…")}
          </p>
          <p className="text-xs text-ink-subtle mt-4">Sluit dit tabblad niet — we kunnen je rapport ook per e-mail bezorgen zodra het klaar is.</p>
        </div>
      </div>
    );
  }

  const counts = SEV_ORDER.map((sev) => ({
    sev,
    count: (grouped[sev] || []).length,
    info: SEVERITY[sev],
  }));

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    toast({ title: "Link gekopieerd", description: "Deel je rapport via e-mail of WhatsApp." });
  }

  const badgeUrl = `${API}/badge/${id}.svg`;

  async function trackShare(channel) {
    try {
      const r = await fetch(`${API}/checks/${id}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel }),
      });
      const j = await r.json().catch(() => ({}));
      if (j?.ok && j?.delivery) {
        setSharedFeedback(j.delivery);
        toast({ title: "Bedankt voor het delen!", description: j.delivery });
      } else if (j?.ok && j?.already_shared) {
        setSharedFeedback("Je 10% kortingscode is onderweg — check je e-mail binnen 24u.");
      } else if (j?.reason) {
        toast({ title: "Bijna gelukt", description: j.reason, variant: "destructive" });
      }
    } catch { /* ignore */ }
  }

  function shareToTwitter() {
    trackShare("twitter");
    const text = encodeURIComponent(`Ik heb mijn ${data.checker_name} laten doorlichten door CheckExpert — score: ${data.score}/100. Check je eigen document op checkexpert.be`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  }
  function shareToLinkedIn() {
    trackShare("linkedin");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + "/")}`, "_blank");
  }
  function shareToWhatsApp() {
    trackShare("whatsapp");
    const text = encodeURIComponent(`Mijn ${data.checker_name} kreeg score ${data.score}/100 van CheckExpert. Check zelf: ${window.location.origin}/`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  }
  function copyBadgeUrl() {
    trackShare("badge_copy");
    navigator.clipboard.writeText(badgeUrl);
    toast({ title: "Badge URL gekopieerd", description: "Voeg deze toe als afbeelding aan blog/socials." });
  }

  async function openNegotiationEmail() {
    setEmailModalOpen(true);
    if (emailDraft) return;
    setEmailLoading(true);
    try {
      const r = await fetch(`${API}/checks/${id}/negotiation-email`, { method: "POST" });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast({ title: "Kon e-mail niet genereren", description: err.detail || "Probeer opnieuw", variant: "destructive" });
        setEmailModalOpen(false);
        return;
      }
      setEmailDraft(await r.json());
    } catch (err) {
      toast({ title: "Mislukt", description: err?.message || "Probeer opnieuw", variant: "destructive" });
      setEmailModalOpen(false);
    } finally {
      setEmailLoading(false);
    }
  }

  function copyEmailToClipboard() {
    if (!emailDraft) return;
    const text = `Onderwerp: ${emailDraft.subject}\n\n${emailDraft.body}`;
    navigator.clipboard.writeText(text);
    toast({ title: "E-mail gekopieerd", description: "Plak in Gmail, Outlook of een ander e-mailprogramma." });
  }

  function openMailto() {
    if (!emailDraft) return;
    const url = `mailto:?subject=${encodeURIComponent(emailDraft.subject)}&body=${encodeURIComponent(emailDraft.body)}`;
    window.location.href = url;
  }

  return (
    <div className="bg-secondary" data-testid="result-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to={`/check/${data.checker_slug}`} className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-6">
          <ChevronLeft className="h-4 w-4" /> Nieuwe {data.checker_name}
        </Link>

        <div className="rounded-3xl bg-white border border-border p-8 sm:p-12 shadow-sm">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 flex justify-center">
              <ScoreGauge score={data.score} accent="#002FA7" />
            </div>
            <div className="lg:col-span-8">
              <p className="text-xs uppercase tracking-wider text-ink-soft font-semibold">
                {data.checker_name} · {data.document_type || "Document"}
              </p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink mt-2 tracking-tight">
                {data.headline || "Je document is geanalyseerd"}
              </h1>
              <p className="text-ink-soft mt-4 leading-relaxed">{data.summary}</p>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="severity-counts">
                {counts.map(({ sev, count, info }) => (
                  <div key={sev} className="rounded-xl border border-border bg-white p-3 text-center">
                    <div className="font-display text-2xl font-bold text-ink">{count}</div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color: info.color }}>
                      {info.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button onClick={copyLink} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink text-white font-semibold text-sm hover:bg-ink-soft" data-testid="copy-link">
                  <Share2 className="h-4 w-4" /> Deel rapport
                </button>
                <a
                  href={`${API}/checks/${id}/pdf`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-border text-ink font-semibold text-sm hover:border-ink-soft"
                  data-testid="download-pdf"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
                <Link to={`/check/${data.checker_slug}/analyse`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-border text-ink font-semibold text-sm hover:border-ink-soft">
                  Nieuwe analyse <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* SHARE BADGE */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white overflow-hidden" data-testid="share-badge-card">
          <div className="grid md:grid-cols-5 gap-0 items-stretch">
            <div className="md:col-span-3 p-8 sm:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-blue-200 text-xs uppercase tracking-wider font-semibold">
                <Sparkles className="h-3 w-3" /> Verdien 10% korting
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3 leading-tight">
                Deel je score, krijg <span className="text-blue-300">10% korting</span> op je volgende check.
              </h2>
              <p className="text-slate-300 mt-3 leading-relaxed text-sm max-w-md">
                Wij maken een anonieme badge (alleen score + check-type, geen documentdata). Plaats hem op LinkedIn, Twitter of WhatsApp — je 10% kortingscode komt binnen 24u in je e-mail toe.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <button onClick={shareToLinkedIn} className="px-4 py-2 rounded-full bg-white text-ink text-sm font-semibold hover:bg-blue-50 transition-colors" data-testid="share-linkedin">LinkedIn</button>
                <button onClick={shareToTwitter} className="px-4 py-2 rounded-full bg-white text-ink text-sm font-semibold hover:bg-blue-50 transition-colors" data-testid="share-twitter">Twitter / X</button>
                <button onClick={shareToWhatsApp} className="px-4 py-2 rounded-full bg-white text-ink text-sm font-semibold hover:bg-blue-50 transition-colors" data-testid="share-whatsapp">WhatsApp</button>
                <button onClick={copyBadgeUrl} className="px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold transition-colors" data-testid="copy-badge">Kopieer badge URL</button>
              </div>
              {sharedFeedback && (
                <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-200 text-xs font-semibold" data-testid="share-feedback">
                  <Sparkles className="h-3.5 w-3.5" /> {sharedFeedback}
                </div>
              )}
            </div>
            <div className="md:col-span-2 bg-slate-950/40 p-6 sm:p-8 flex items-center justify-center border-t md:border-t-0 md:border-l border-white/10">
              <img src={badgeUrl} alt="CheckExpert score badge" className="rounded-2xl shadow-2xl w-full max-w-[300px] aspect-square object-contain" />
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center gap-2 mb-5">
            <ListChecks className="h-5 w-5 text-[#002FA7]" />
            <h2 className="font-display text-2xl font-bold text-ink tracking-tight">
              Bevindingen ({data.findings.length})
            </h2>
          </div>
          <div className="space-y-4" data-testid="findings-list">
            {SEV_ORDER.flatMap((sev) =>
              (grouped[sev] || []).map((f, i) => <FindingItem key={`${sev}-${i}`} finding={f} />)
            )}
          </div>
        </div>

        {data.negotiation_tips?.length > 0 && (
          <div className="mt-10 rounded-3xl bg-slate-900 text-white p-8 sm:p-10">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-300" />
                <h2 className="font-display text-2xl font-bold tracking-tight">Onderhandelingstips</h2>
              </div>
              <button
                onClick={openNegotiationEmail}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-ink text-sm font-semibold hover:bg-blue-50 transition-colors"
                data-testid="generate-email-btn"
              >
                <MessageSquare className="h-4 w-4" /> Genereer e-mail naar de andere partij
              </button>
            </div>
            <ol className="space-y-3 text-slate-200">
              {data.negotiation_tips.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-blue-300/20 text-blue-200 font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {data.next_steps?.length > 0 && (
          <div className="mt-10 rounded-3xl bg-white border border-border p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink className="h-5 w-5 text-[#002FA7]" />
              <h2 className="font-display text-2xl font-bold text-ink tracking-tight">Volgende stappen</h2>
            </div>
            <ul className="space-y-3 text-ink">
              {data.next_steps.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="h-2 w-2 mt-2.5 rounded-full bg-[#002FA7]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-10 text-xs text-ink-subtle text-center italic max-w-2xl mx-auto">
          CheckExpert geeft een eerste, onafhankelijke analyse op basis van actuele BE/NL wetgeving en marktstandaarden. Bij hoge bedragen of complexe geschillen blijft het raadplegen van een specialist (advocaat, notaris, accountant of andere expert) aangewezen.
        </p>
      </div>

      {/* Negotiation Email Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEmailModalOpen(false)} data-testid="email-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-semibold text-ink">E-mail naar de andere partij</h3>
                <p className="text-xs text-ink-soft mt-0.5">Beleefd, concreet en kant-en-klaar. Pas aan en verstuur.</p>
              </div>
              <button onClick={() => setEmailModalOpen(false)} className="text-ink-soft hover:text-ink text-xl">✕</button>
            </div>
            <div className="p-6">
              {emailLoading && (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto" />
                  <p className="text-sm text-ink-soft mt-3">E-mail wordt opgesteld op basis van je bevindingen…</p>
                </div>
              )}
              {emailDraft && !emailLoading && (
                <>
                  <div className="rounded-xl border border-border bg-slate-50 p-4 mb-4">
                    <div className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Onderwerp</div>
                    <div className="font-semibold text-ink mt-1">{emailDraft.subject}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-white p-5 whitespace-pre-wrap text-sm text-ink leading-relaxed" data-testid="email-body">
                    {emailDraft.body}
                  </div>
                  <div className="mt-5 flex gap-2 flex-wrap">
                    <button onClick={copyEmailToClipboard} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold" data-testid="copy-email">
                      Kopieer e-mail
                    </button>
                    <button onClick={openMailto} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-ink text-sm font-semibold">
                      Open in mail-programma
                    </button>
                  </div>
                  <p className="text-xs text-ink-subtle italic mt-4">Pas de e-mail gerust aan naar je eigen stijl. We hebben hem opgesteld op basis van de bevindingen van je {data.checker_name}.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
